document.documentElement.classList.remove('no-js');

// Keep Shopify's native cart validation; do not use update.js to bypass stock checks.
const initializeCartUpdates = (root = document) => {
  root.querySelectorAll('form#cart').forEach((form) => {
    const feedback = form.querySelector('[data-cart-update-error]');
    if (!feedback || form.dataset.cartUpdateReady === 'true') return;
    form.dataset.cartUpdateReady = 'true';
    let busy = false;
    let refreshRequired = false;
    form.addEventListener('submit', async (event) => {
      if (busy || refreshRequired) { event.preventDefault(); return; }
      if (event.submitter?.name === 'checkout') return;
      const destination = new URL(form.action, window.location.href);
      if (destination.origin !== window.location.origin || !/\/cart\/?$/.test(destination.pathname)) return;
      event.preventDefault();
      const body = new FormData(form);
      // Update only: never allow an implicit checkout value in this request.
      body.delete('checkout');
      const controls = [...form.querySelectorAll('button[type="submit"]')];
      busy = true;
      form.setAttribute('aria-busy', 'true');
      controls.forEach((button) => { button.disabled = true; });
      form.querySelectorAll('.additional-checkout-buttons').forEach((element) => {
        element.hidden = true;
        element.inert = true;
        element.style.setProperty('display', 'none', 'important');
      });
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(destination.href, {
          method: 'POST', credentials: 'same-origin', headers: { Accept: 'text/html' }, body, signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart rejected');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!page.querySelector('main [data-cart-state]')) throw new Error('Cart not confirmed');
        // Shopify remains authoritative for quantities, discounts, taxes and totals.
        window.location.assign(destination.href);
      } catch (_) {
        // A request might have partially succeeded. Do not replay it or claim no changes.
        refreshRequired = true;
        feedback.hidden = false;
        feedback.focus();
      } finally {
        window.clearTimeout(timeout);
        form.removeAttribute('aria-busy');
        // Keep all payment paths locked until the confirmed page reloads.
      }
    });
  });
};
initializeCartUpdates();
document.addEventListener('shopify:section:load', (event) => initializeCartUpdates(event.target));

// Only recognise standalone model codes. Never rewrite catalogue filters, quoted
// phrases or normal multi-word searches. Preserve every model/phase suffix.
const modelSearchAliases = (value) => {
  const term = String(value || '').trim();
  if (term.length > 80 || !/^[a-z0-9]+(?:[-_][a-z0-9]+)+$/i.test(term) || !/[a-z]/i.test(term) || !/\d/.test(term)) return [];
  const canonical = term.toUpperCase().replaceAll('_', '-');
  // The imported catalogue also contains mixed codes such as JWBS_10-M.
  return [...new Set([canonical, canonical.replace('-', '_'), canonical.replace(/-([^-]*)$/, '_$1'), canonical.replaceAll('-', '_')])];
};
const modelSearchQuery = (value) => {
  const aliases = modelSearchAliases(value);
  // Unquoted OR terms trigger broad token matches (JWBS-10 matched JWBS-18).
  return aliases.length ? aliases.flatMap((alias) => [`title:"${alias}"`, `variants.sku:"${alias}"`]).join(' OR ') : String(value || '').trim();
};
const modelSearchMatches = (term, title, skus = '') => {
  const canonical = modelSearchAliases(term)[0];
  if (!canonical) return true;
  const codes = [String(title || ''), ...String(skus || '').split('|')]
    .flatMap((value) => value.toUpperCase().replaceAll('_', '-').match(/[A-Z0-9]+(?:-[A-Z0-9]+)+/g) || []);
  // Keep the requested model and suffix variants, not typo-tolerant neighbours.
  return codes.some((code) => code === canonical || code.startsWith(canonical + '-'));
};
const modelSearchIncludesProducts = (types) => {
  const requested = String(types || '').split(',').map((type) => type.trim()).filter(Boolean);
  return !requested.length || requested.includes('product');
};
const initializeModelSearch = (root = document) => {
  root.querySelectorAll('form[role="search"]').forEach((form) => {
    const input = form.querySelector('input[name="q"]');
    if (!input || form.dataset.modelSearchReady === 'true') return;
    form.dataset.modelSearchReady = 'true';
    // Change the submitted query, not the text the shopper is typing.
    form.addEventListener('formdata', (event) => {
      // Learning and other explicitly scoped searches must keep their scope.
      if (!modelSearchIncludesProducts(event.formData.get('type'))) return;
      if (!modelSearchAliases(input.value).length) return;
      event.formData.set('q', modelSearchQuery(input.value));
      event.formData.set('type', 'product');
      event.formData.set('options[prefix]', 'last');
    });
  });
  const page = root.querySelector('[data-model-search-page]');
  if (!page) return;
  if (page.dataset.nativeCategoryUrl) {
    const destination = new URL(page.dataset.nativeCategoryUrl, window.location.origin);
    if (destination.origin === window.location.origin && destination.pathname.startsWith('/collections/')) window.location.replace(destination.href);
    return;
  }
  // Also handle old/bookmarked plain-code search URLs. The generated OR query
  // is not a model code, so this cannot redirect a second time or alter filters.
  const aliases = modelSearchAliases(page.dataset.searchQuery);
  if (aliases.length) {
    const destination = new URL(window.location.href);
    if (!modelSearchIncludesProducts(destination.searchParams.get('type'))) return;
    destination.searchParams.set('q', modelSearchQuery(page.dataset.searchQuery));
    destination.searchParams.set('type', 'product');
    destination.searchParams.set('options[prefix]', 'last');
    destination.searchParams.delete('page');
    window.location.replace(destination.href);
  }
};
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search-results], #predictive-search');
    this.abortController = null;
    this.timer = null;
    this.activeIndex = -1;
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.close();
      this.results.innerHTML = '';
      this.timer = window.setTimeout(() => this.onChange(), 180);
    });
    this.input.addEventListener('focus', () => {
      if (this.results.innerHTML.trim()) this.open();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveSelection(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveSelection(-1);
      } else if (event.key === 'Enter' && event.target === this.input && !event.isComposing) {
        const activeLink = this.activeIndex >= 0 ? this.getOptions()[this.activeIndex]?.querySelector('a') : null;
        event.preventDefault();
        if (activeLink) activeLink.click();
        else if (this.input.value.trim()) this.input.form?.requestSubmit();
      }
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  async onChange() {
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.close();
      this.results.innerHTML = '';
      return;
    }
    if (this.abortController) this.abortController.abort();
    const controller = new AbortController();
    this.abortController = controller;
    try {
      const aliases = modelSearchAliases(term);
      const queries = aliases.length ? aliases : [term];
      // Predictive search does not understand storefront OR syntax. Request
      // literal aliases separately, then merge the actual returned products.
      const sections = await Promise.all(queries.map(async (query) => {
        try {
          const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku&section_id=predictive-search`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) return null;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          return html.querySelector('#shopify-section-predictive-search');
        } catch (error) {
          if (controller.signal.aborted) throw error;
          return null; // A failed alternate spelling must not hide a good result.
        }
      }));
      if (controller.signal.aborted || controller !== this.abortController || this.input.value.trim() !== term) return;
      const usable = sections.filter(Boolean);
      const section = usable.find((item) => item.querySelector('[role="option"]')) || usable[0];
      if (queries.length > 1 && section) {
        const list = section.querySelector('[role="group"]');
        if (list) {
          const seen = new Set();
          const options = [];
          usable.forEach((item) => item.querySelectorAll('[role="option"]').forEach((option) => {
            if (!modelSearchMatches(term, option.dataset.modelTitle, option.dataset.modelSkus)) return;
            const href = option.querySelector('a')?.getAttribute('href');
            if (!href) return;
            const path = new URL(href, window.location.href).pathname;
            if (seen.has(path) || options.length >= 6) return;
            seen.add(path);
            const copy = option.cloneNode(true);
            copy.id = `predictive-search-option-${options.length + 1}`;
            copy.setAttribute('aria-selected', 'false');
            options.push(copy);
          }));
          list.replaceChildren(...options);
          if (!options.length) {
            const empty = document.createElement('p');
            empty.className = 'predictive-search__empty';
            empty.textContent = 'No products found. Try a brand, model or tool type.';
            list.replaceWith(empty);
          }
        }
      }
      const allResults = section?.querySelector('.predictive-search__all');
      if (allResults && this.input.form) {
        const destination = new URL(this.input.form.action, window.location.href);
        destination.searchParams.set('q', modelSearchQuery(term));
        destination.searchParams.set('type', 'product');
        destination.searchParams.set('options[prefix]', 'last');
        allResults.setAttribute('href', destination.href);
        allResults.textContent = `View all results for “${aliases[0] || term}” →`;
      }
      const markup = section?.innerHTML.trim();
      if (!markup) {
        this.results.innerHTML = '';
        this.close();
        return;
      }
      this.results.innerHTML = markup;
      this.activeIndex = -1;
      this.open();
    } catch (error) {
      if (error.name !== 'AbortError' && controller === this.abortController) this.close();
    }
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  getOptions() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  moveSelection(direction) {
    const options = this.getOptions();
    if (!options.length || this.results.hidden) return;
    this.activeIndex = this.activeIndex < 0
      ? (direction > 0 ? 0 : options.length - 1)
      : (this.activeIndex + direction + options.length) % options.length;
    options.forEach((option, index) => {
      const active = index === this.activeIndex;
      option.setAttribute('aria-selected', String(active));
      if (active) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  close() {
    window.clearTimeout(this.timer);
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    this.getOptions().forEach((option) => option.setAttribute('aria-selected', 'false'));
  }
}
customElements.define('predictive-search', PredictiveSearch);
initializeModelSearch();
document.addEventListener('shopify:section:load', (event) => initializeModelSearch(event.target));

const collapseMobileFacets = (root = document) => {
  if (!window.matchMedia('(max-width: 900px)').matches) return;
  root.querySelectorAll('.facets .facet').forEach((facet) => { facet.open = false; });
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => collapseMobileFacets());
} else {
  collapseMobileFacets();
}
document.addEventListener('shopify:section:load', (event) => collapseMobileFacets(event.target));

const initializeDesktopNavigation = (root = document) => {
  const menus = [...root.querySelectorAll('.desktop-nav__details')];
  menus.forEach((menu) => {
    if (menu.dataset.navigationReady === 'true') return;
    menu.dataset.navigationReady = 'true';
    menu.addEventListener('toggle', () => {
      if (!menu.open) return;
      document.querySelectorAll('.desktop-nav__details[open]').forEach((otherMenu) => {
        if (otherMenu !== menu) otherMenu.open = false;
      });
    });
  });
};
initializeDesktopNavigation();
document.addEventListener('shopify:section:load', (event) => initializeDesktopNavigation(event.target));
document.addEventListener('click', (event) => {
  if (event.target.closest('.desktop-nav__details')) return;
  document.querySelectorAll('.desktop-nav__details[open]').forEach((menu) => { menu.open = false; });
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const openMenu = document.querySelector('.desktop-nav__details[open]');
  if (openMenu) {
    openMenu.open = false;
    openMenu.querySelector('summary')?.focus();
    return;
  }
  const openMobileMenu = document.querySelector('.mobile-menu[open]');
  if (!openMobileMenu) return;
  openMobileMenu.open = false;
  openMobileMenu.querySelector(':scope > summary')?.focus();
});

const initializeMobileNavigation = (root = document) => {
  root.querySelectorAll('.mobile-menu').forEach((menu) => {
    if (menu.dataset.navigationReady === 'true') return;
    menu.dataset.navigationReady = 'true';
    menu.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', () => { menu.open = false; });
    });
  });
};
initializeMobileNavigation();
document.addEventListener('shopify:section:load', (event) => initializeMobileNavigation(event.target));

class LocalizationForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.querySelectorAll('select').forEach((select) => {
      select.addEventListener('change', () => this.form.requestSubmit());
    });
  }
}
customElements.define('localization-form', LocalizationForm);

class CampaignHero extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-hero-dot]')];
    this.previousButton = this.querySelector('[data-hero-prev]');
    this.nextButton = this.querySelector('[data-hero-next]');
    this.pauseButton = this.querySelector('[data-hero-pause]');
    this.status = this.querySelector('[data-hero-status]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.delay = Math.max(3000, Number(this.dataset.delay) || 7500);
    this.paused = this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));
    this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true));
    this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));
    if (this.pauseButton) this.pauseButton.addEventListener('click', () => this.togglePause());
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => { if (!this.paused) this.start(); });
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => { if (!this.paused) this.start(); });
    this.updatePauseButton();
    if (!this.paused && this.slides.length > 1) this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  show(index, userInitiated = false) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      if (i === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (this.status) this.status.textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
    if (userInitiated && !this.paused) this.start();
  }

  start() {
    this.stop();
    this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = undefined;
  }

  togglePause() {
    this.paused = !this.paused;
    this.updatePauseButton();
    if (this.paused) this.stop(); else this.start();
  }

  updatePauseButton() {
    if (!this.pauseButton) return;
    const icon = this.pauseButton.querySelector('span');
    if (icon) icon.textContent = this.paused ? '▶' : 'Ⅱ';
    else this.pauseButton.textContent = this.paused ? '▶' : 'Ⅱ';
    this.pauseButton.setAttribute('aria-label', this.paused ? 'Play hero slideshow' : 'Pause hero slideshow');
  }
}
customElements.define('campaign-hero', CampaignHero);

class ProductDetails extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-variant-select]');
    if (this.select) this.select.addEventListener('change', () => this.updateVariant());
    const availability = this.querySelector('[data-availability-request]');
    const availabilityForm = availability?.closest('form');
    if (availability && !availability.hidden) availability.querySelector('[data-availability-feedback]')?.focus();
    if (availabilityForm && availabilityForm.dataset.availabilityReady !== 'true') {
      availabilityForm.dataset.availabilityReady = 'true';
      availabilityForm.addEventListener('submit', (event) => {
        if (this.select) this.updateAvailabilityContact(this.select.options[this.select.selectedIndex], false);
        if (availability.hidden || availability.querySelector('[data-availability-fields]')?.disabled) event.preventDefault();
      });
    }
  }

  updateVariant() {
    const option = this.select.options[this.select.selectedIndex];
    const available = option.dataset.available === 'true';
    const button = this.querySelector('[data-add-to-cart]');
    const price = this.querySelector('[data-product-price]');
    const priceEx = this.querySelector('[data-product-price-ex]');
    const priceExRow = this.querySelector('[data-product-price-ex-row]');
    const vatLabel = this.querySelector('[data-product-vat-label]');
    const compare = this.querySelector('[data-product-compare]');
    const stock = this.querySelector('[data-product-stock]');
    const stockIndicator = this.querySelector('[data-product-stock-indicator]');
    const showExVat = option.dataset.taxable === 'true' && Boolean(option.dataset.priceEx);
    if (price) price.textContent = option.dataset.price || '';
    if (priceEx) priceEx.textContent = showExVat ? option.dataset.priceEx : '';
    if (priceExRow) priceExRow.hidden = !showExVat;
    if (vatLabel) vatLabel.textContent = option.dataset.taxable === 'true' ? (option.dataset.vatLabel || 'Taxes shown where applicable') : 'VAT not charged';
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
    const stockLabel = option.dataset.stockLabel?.trim() || (available ? 'Delivery date to be confirmed' : 'Currently unavailable');
    if (stock) stock.textContent = stockLabel;
    stockIndicator?.classList.toggle('stock-dot--order', ['Delivery date to be confirmed', 'Available to order — delivery date to be confirmed'].includes(stockLabel));
    stockIndicator?.classList.toggle('stock-dot--out', stockLabel === 'Currently unavailable');
    stockIndicator?.classList.toggle('stock-dot--unknown', !['Delivery date to be confirmed', 'Available to order — delivery date to be confirmed', 'Supplier stock listed', 'Currently unavailable'].includes(stockLabel));
    if (button) {
      button.disabled = !available;
      button.textContent = available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
      if (this.querySelector('[data-order-enquiry]')) button.hidden = !available;
    }
    this.querySelectorAll('[data-order-enquiry]').forEach(element => { element.hidden = available; });
    this.updatePickup(option);
    this.updateAvailabilityContact(option);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  }

  updateAvailabilityContact(option, clearFeedback = true) {
    const availability = this.querySelector('[data-availability-request]');
    if (!availability || !option) return;
    const eligible = option.dataset.available === 'false' || option.dataset.availabilityContact === 'true';
    const contactLabel = ['Supplier stock listed', 'Delivery date to be confirmed', 'Available to order — delivery date to be confirmed', 'Contact us for availability and delivery'].includes(option.dataset.stockLabel?.trim()) ? 'Ask about delivery and ordering' : 'Contact me when available';
    availability.querySelectorAll('[data-availability-label]').forEach((label) => { label.textContent = contactLabel; });
    const fields = availability.querySelector('[data-availability-fields]');
    availability.hidden = !eligible;
    if (fields) {
      fields.disabled = !eligible;
      if (clearFeedback) fields.hidden = false;
    }
    if (clearFeedback) {
      availability.querySelectorAll('[data-availability-feedback]').forEach((feedback) => { feedback.hidden = true; });
      const email = availability.querySelector('[name="contact[email]"]');
      email?.removeAttribute('aria-invalid');
      const description = email?.getAttribute('aria-describedby');
      if (description) email.setAttribute('aria-describedby', description.split(/\s+/).filter((id) => id !== `${availability.id}-Errors`).join(' '));
    }

    const title = option.dataset.variantTitle || '';
    const sku = option.dataset.variantSku || '';
    const variantLabel = availability.querySelector('[data-availability-variant]');
    const skuLabel = availability.querySelector('[data-availability-sku-label]');
    const skuValue = availability.querySelector('[data-availability-sku]');
    if (variantLabel) variantLabel.textContent = title;
    if (skuLabel) skuLabel.hidden = !sku;
    if (skuValue) skuValue.textContent = sku;

    const url = new URL(availability.dataset.productUrl, window.location.href);
    url.searchParams.set('variant', option.value);
    const productUrl = url.pathname + url.search;
    const values = {
      variant_id: option.value,
      variant_title: title,
      variant_sku: sku,
      product_url: productUrl,
      body: `Please check availability and contact me about ${availability.dataset.productTitle} (${availability.dataset.productHandle}), option ${title}, SKU ${sku}. Product: ${productUrl}. This is an availability enquiry, not a marketing subscription.`,
    };
    Object.entries(values).forEach(([name, value]) => {
      const input = availability.querySelector(`[name="contact[${name}]"]`);
      if (input) input.value = value;
    });
    const returnTo = availability.closest('form')?.querySelector('[name="return_to"]');
    if (returnTo) returnTo.value = `${productUrl}#${availability.id}`;
  }

  updatePickup(option) {
    const pickup = this.querySelector('[data-pickup-availability]');
    if (!pickup) return;
    const configured = option.dataset.pickupConfigured === 'true';
    const available = option.dataset.pickupAvailable === 'true';
    const locationName = option.dataset.pickupLocation || '';
    const pickupTime = option.dataset.pickupTime || '';
    const lead = pickup.querySelector('[data-pickup-lead]');
    const location = pickup.querySelector('[data-pickup-location]');
    const time = pickup.querySelector('[data-pickup-time]');

    pickup.classList.toggle('product-pickup--available', available);
    if (lead) {
      if (available) lead.textContent = 'Pickup available at';
      else if (configured) lead.textContent = 'Pickup currently unavailable at';
      else lead.textContent = 'Store pickup is not available for this option.';
    }
    if (location) {
      location.textContent = locationName;
      location.hidden = !configured || !locationName;
    }
    if (time) {
      time.textContent = pickupTime ? `— ${pickupTime}` : '';
      time.hidden = !available || !pickupTime;
    }
  }
}
customElements.define('product-details', ProductDetails);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = parsed.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim()) this.innerHTML = recommendations.innerHTML;
      })
      .catch(() => {});
  }
}
customElements.define('product-recommendations', ProductRecommendations);

const initializeCollectionSorting = (root = document) => {
  root.querySelectorAll('[data-sort-select]').forEach((select) => {
    if (select.dataset.sortReady === 'true') return;
    select.dataset.sortReady = 'true';
    select.addEventListener('change', () => select.form?.submit());
  });
};
initializeCollectionSorting();
document.addEventListener('shopify:section:load', (event) => initializeCollectionSorting(event.target));

const supportToggle = document.querySelector('[data-support-toggle]');
const supportPanel = document.querySelector('[data-support-panel]');
const supportScrim = document.querySelector('[data-support-scrim]');
const supportClose = document.querySelector('[data-support-close]');
const setSupportState = (open) => {
  if (!supportToggle || !supportPanel || !supportScrim) return;
  supportToggle.setAttribute('aria-expanded', String(open));
  supportPanel.setAttribute('aria-hidden', String(!open));
  supportPanel.classList.toggle('is-open', open);
  supportScrim.hidden = !open;
  document.body.classList.toggle('no-scroll', open);
  if (open) supportClose?.focus(); else supportToggle.focus();
};
supportToggle?.addEventListener('click', () => setSupportState(supportToggle.getAttribute('aria-expanded') !== 'true'));
supportClose?.addEventListener('click', () => setSupportState(false));
supportScrim?.addEventListener('click', () => setSupportState(false));
document.addEventListener('keydown', (event) => {
  if (!supportPanel?.classList.contains('is-open')) return;
  if (event.key === 'Escape') {
    setSupportState(false);
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = [...supportPanel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((element) => element.getClientRects().length > 0);
  if (!focusable.length) {
    event.preventDefault();
    supportPanel.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

if ('IntersectionObserver' in window && document.body.classList.contains('reveal-enabled')) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  document.querySelectorAll('[data-reveal]').forEach((element) => observer.observe(element));
}
